import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Lock, LockOpen } from 'lucide-react';
import { useState } from 'react';
import { Field, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiError, api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import {
  monthLabel,
  monthShort,
  periodsToLock,
  periodsToUnlock,
  rangeLabel,
  usePeriods,
  type FiscalYear,
  type Period,
  type PeriodsOverview,
} from '@/lib/periods';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/accounting/periods/')({ component: PeriodsPage });

const STATUS: Record<FiscalYear['status'], { label: string; variant: 'outline' | 'secondary' }> = {
  open: { label: 'Open', variant: 'outline' },
  locked: { label: 'All months locked', variant: 'outline' },
  closed: { label: 'Closed', variant: 'secondary' },
};

/** Screen A5 (document 06): financial years with a row of month locks, lock/unlock with reason. */
function PeriodsPage() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const periods = usePeriods();
  const [action, setAction] = useState<{ kind: 'lock' | 'unlock'; period: Period } | null>(null);
  const addYear = useMutation({
    mutationFn: () => api<PeriodsOverview>(`/orgs/${orgId}/fiscal-years`, { method: 'POST' }),
    onSuccess: async (o) => {
      await qc.invalidateQueries({ queryKey: ['periods', orgId] });
      const last = o.fiscalYears[o.fiscalYears.length - 1];
      toast(`Financial year ${last?.label ?? ''} added.`);
    },
    onError: (e) => toast(e.message),
  });

  if (!orgId)
    return <EmptyState title="Select a client" hint="Choose a client to see its periods." />;
  if (periods.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (periods.isError)
    return <EmptyState title="Could not load periods" hint={periods.error.message} />;
  const o = periods.data;
  const years = [...o.fiscalYears].reverse();

  return (
    <div>
      <PageHeader
        title="Periods and close"
        description="Lock the months you have finished so nothing dated in them can be posted, reversed or changed. Locking a month also locks every month before it."
        actions={
          o.canManage ? (
            <Button variant="outline" disabled={addYear.isPending} onClick={() => addYear.mutate()}>
              Add financial year
            </Button>
          ) : null
        }
      />
      <div
        className="mb-6 flex items-center gap-3 rounded-lg border bg-card px-4 py-3"
        data-testid="lock-date"
      >
        {o.lockDate ? (
          <Lock className="size-5 text-primary" aria-hidden />
        ) : (
          <LockOpen className="size-5 text-muted-foreground" aria-hidden />
        )}
        <div className="text-sm">
          {o.lockDate ? (
            <>
              <div className="font-medium">Books closed through {formatDate(o.lockDate)}</div>
              <div className="text-muted-foreground">
                Locked by {o.lockedByName ?? 'an accountant'}
                {o.lockedAt ? ` on ${formatDate(o.lockedAt)}` : ''}. Postings dated on or before
                this date are refused.
              </div>
            </>
          ) : (
            <div className="font-medium">No months are locked yet.</div>
          )}
        </div>
        {!o.canManage ? (
          <p className="ml-auto text-xs text-muted-foreground">
            Locking and unlocking needs close rights for this client.
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-5">
        {years.map((fy) => (
          <FiscalYearCard
            key={fy.id}
            fy={fy}
            canManage={o.canManage}
            onAction={(kind, period) => setAction({ kind, period })}
          />
        ))}
      </div>
      <LockDialog overview={o} action={action} onClose={() => setAction(null)} />
    </div>
  );
}

function FiscalYearCard({
  fy,
  canManage,
  onAction,
}: {
  fy: FiscalYear;
  canManage: boolean;
  onAction: (kind: 'lock' | 'unlock', p: Period) => void;
}) {
  const status = STATUS[fy.status];
  const hasEntries = fy.periods.some((p) => p.entryCount > 0);
  return (
    <section
      className="rounded-lg border bg-card"
      aria-label={`Financial year ${fy.label}`}
      data-testid={`fy-${fy.label}`}
    >
      <header className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <h2 className="text-base font-semibold">Financial year {fy.label}</h2>
        <span className="text-sm text-muted-foreground">
          {formatDate(fy.startDate)} – {formatDate(fy.endDate)}
        </span>
        <Badge variant={status.variant}>{status.label}</Badge>
        {fy.status === 'closed' ? (
          <span className="text-xs text-muted-foreground">
            Closed by {fy.closedByName ?? 'an accountant'}
            {fy.closedAt ? ` on ${formatDate(fy.closedAt)}` : ''}
            {fy.closeEntryId ? (
              <>
                {' · '}
                <Link
                  to="/accounting/journal-entries/$entryId"
                  params={{ entryId: fy.closeEntryId }}
                  className="text-primary hover:underline"
                >
                  close entry no. {fy.closeEntryNo}
                </Link>
              </>
            ) : null}
          </span>
        ) : null}
        <div className="ml-auto">
          {fy.status === 'closed' || hasEntries || fy.status === 'locked' ? (
            <Button asChild variant={fy.status === 'locked' ? 'default' : 'outline'} size="sm">
              <Link to="/accounting/periods/$fiscalYearId/close" params={{ fiscalYearId: fy.id }}>
                {fy.status === 'closed' ? 'View close' : 'Close year'}
              </Link>
            </Button>
          ) : null}
        </div>
      </header>
      <ol className="grid grid-cols-3 gap-2 p-4 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-12">
        {fy.periods.map((p) => (
          <li key={p.id}>
            <MonthTile
              period={p}
              closedYear={fy.status === 'closed'}
              canManage={canManage}
              onAction={onAction}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

function MonthTile({
  period: p,
  closedYear,
  canManage,
  onAction,
}: {
  period: Period;
  closedYear: boolean;
  canManage: boolean;
  onAction: (kind: 'lock' | 'unlock', p: Period) => void;
}) {
  const label = monthLabel(p.startDate);
  const tile = (
    <button
      type="button"
      className={cn(
        'flex w-full flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-left text-xs transition-colors',
        p.locked ? 'border-primary/30 bg-primary/5' : 'bg-background hover:bg-accent',
        !canManage && 'cursor-default',
      )}
      aria-label={`${label}, ${p.locked ? 'locked' : 'open'}`}
      data-testid={`month-${p.startDate.slice(0, 7)}`}
      title={
        p.locked
          ? `Locked by ${p.lockedByName ?? 'an accountant'}${p.lockedAt ? ` on ${formatDate(p.lockedAt)}` : ''}: ${p.lockReason ?? ''}`
          : undefined
      }
    >
      <span className="flex w-full items-center justify-between">
        <span className="text-sm font-medium">{monthShort(p.startDate)}</span>
        {p.locked ? (
          <Lock className="size-4 text-primary" aria-hidden />
        ) : (
          <LockOpen className="size-4 text-muted-foreground" aria-hidden />
        )}
      </span>
      <span className={p.locked ? 'font-medium text-primary' : 'text-muted-foreground'}>
        {p.locked ? 'Locked' : 'Open'}
      </span>
      <span className="text-muted-foreground">
        {p.entryCount === 0
          ? 'No entries'
          : `${p.entryCount} ${p.entryCount === 1 ? 'entry' : 'entries'}`}
      </span>
      {p.draftCount > 0 ? <span className="text-destructive">{p.draftCount} drafts</span> : null}
      {p.reviewCount > 0 ? <span className="text-warning">{p.reviewCount} to review</span> : null}
    </button>
  );
  if (!canManage) return tile;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{tile}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-50 max-w-72">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {p.locked && p.lockReason ? (
          <p className="px-2 pb-1.5 text-xs text-muted-foreground">
            Locked by {p.lockedByName ?? 'an accountant'}
            {p.lockedAt ? ` on ${formatDate(p.lockedAt)}` : ''}: {p.lockReason}
          </p>
        ) : null}
        <DropdownMenuSeparator />
        {p.locked ? (
          <DropdownMenuItem
            disabled={closedYear}
            onSelect={() => onAction('unlock', p)}
            title={closedYear ? 'Reopen the financial year first' : undefined}
          >
            Unlock from {label}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => onAction('lock', p)}>
            Lock through {label}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** ConfirmDialog with a typed reason (document 05): lock and unlock are audited. */
function LockDialog({
  overview,
  action,
  onClose,
}: {
  overview: PeriodsOverview;
  action: { kind: 'lock' | 'unlock'; period: Period } | null;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const affected = action
    ? action.kind === 'lock'
      ? periodsToLock(overview, action.period)
      : periodsToUnlock(overview, action.period)
    : [];
  const m = useMutation({
    mutationFn: (confirmWarnings: boolean) =>
      api<PeriodsOverview>(`/orgs/${orgId}/periods/${action!.kind}`, {
        method: 'POST',
        body: JSON.stringify(
          action!.kind === 'lock'
            ? { through: action!.period.startDate, reason: reason.trim(), confirmWarnings }
            : { from: action!.period.startDate, reason: reason.trim() },
        ),
      }),
    onSuccess: async (o) => {
      qc.setQueryData(['periods', orgId], o);
      await qc.invalidateQueries({ queryKey: ['periods', orgId] });
      toast(
        action!.kind === 'lock'
          ? `Books closed through ${o.lockDate ? formatDate(o.lockDate) : ''}.`
          : `${rangeLabel(affected)} unlocked.`,
      );
      close();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'PERIOD_WARNINGS') {
        const items = (e.details as { items?: string[] } | undefined)?.items;
        setWarnings(items ?? [e.message]);
      }
    },
  });
  const close = () => {
    setReason('');
    setWarnings(null);
    m.reset();
    onClose();
  };
  if (!action) return null;
  const label = monthLabel(action.period.startDate);
  const lock = action.kind === 'lock';
  const tooShort = reason.trim().length < 3;
  const error = m.error && !(m.error instanceof ApiError && m.error.code === 'PERIOD_WARNINGS');
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{lock ? `Lock through ${label}?` : `Unlock from ${label}?`}</DialogTitle>
          <DialogDescription>
            {lock
              ? `Nothing dated on or before ${formatDate(action.period.endDate)} can be posted, reversed or changed once it is locked.`
              : 'These months open for posting again. Lock them again when the changes are done.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <p className="text-sm">
            {affected.length === 1 ? 'Month' : `${affected.length} months`}:{' '}
            <span className="font-medium">{rangeLabel(affected)}</span>
          </p>
          <Field label="Reason" hint="Recorded with the period and in the audit log.">
            {(id) => (
              <Textarea
                id={id}
                value={reason}
                autoFocus
                placeholder={
                  lock ? 'For example: VAT return filed, month reviewed' : 'Why it needs to open'
                }
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {warnings ? (
            <div
              role="alert"
              className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm"
            >
              <p className="font-medium">Some items in these months still need attention:</p>
              <ul className="mt-1 list-disc pl-4">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {m.error.message}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            variant={lock ? 'default' : 'destructive'}
            disabled={tooShort || m.isPending}
            onClick={() => m.mutate(Boolean(warnings))}
          >
            {m.isPending
              ? lock
                ? 'Locking…'
                : 'Unlocking…'
              : lock
                ? warnings
                  ? 'Lock anyway'
                  : 'Lock'
                : 'Unlock'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
